import { useEffect, useRef, useState, Fragment, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, WS_BASE } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { Radio, History, Crosshair, X as XIcon, Play as PlayIcon } from "lucide-react";
import { TILE_LAYERS, resolveLayer, MapStyleToggle, HeadingToggle, useDeviceHeading, RotateFrame, InvalidateOnChange } from "@/components/MapControls";
import { FleetReplayBar } from "@/components/FleetReplayBar";

// Fix Leaflet default icon paths (breaks with webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const plantIcon = L.divIcon({
  html: '<div style="background:#F57C00;width:22px;height:22px;border:2px solid #000;display:flex;align-items:center;justify-content:center;color:#000;font-weight:900;font-size:11px;font-family:JetBrains Mono">P</div>',
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const siteIcon = L.divIcon({
  html: '<div style="background:#FAFAFA;width:14px;height:14px;border:2px solid #F57C00;border-radius:50%"></div>',
  className: "",
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const meIcon = L.divIcon({
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid #FFF;box-shadow:0 0 0 4px rgba(37,99,235,.3)"></div>',
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// v3.15f · Truck icon matches the War Room — circle-pin with the mixer glyph
// in the middle, colour keyed off the same STATUS_TINT palette so operators
// see the same visual language across both maps.
const STATUS_COLOR = {
  available:      "#22C55E",
  loading:        "#F97316",
  slumping:       "#FBBF24",
  en_route:       "#3B82F6",
  on_site:        "#A855F7",
  discharging:    "#A855F7",
  returning:      "#0EA5E9",
  out_of_service: "#71717A",
  maintenance:    "#71717A",
};

function truckIcon(truck) {
  const color = STATUS_COLOR[truck.status] || "#F97316";
  const number = (truck.number || "").replace(/^T-?/i, "");
  // War Room-style circle pin: coloured disc, black stroke, small mixer glyph,
  // number badge underneath. Always upright (no heading rotation) so it stays
  // readable in every scroll direction.
  return L.divIcon({
    html: `
      <div style="position:relative;width:40px;height:52px">
        <svg viewBox="0 0 40 40" width="40" height="40" style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.5))">
          <circle cx="20" cy="20" r="14" fill="${color}" stroke="#0A0A0B" stroke-width="2"/>
          <path d="M11 21h4v-5h6v5h5l-1-4h-4v-3h-6l-1 3H12z" fill="#0A0A0B"/>
        </svg>
        <div style="position:absolute;top:34px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:#FFF;font-family:JetBrains Mono;font-weight:900;font-size:10px;padding:1px 4px;white-space:nowrap;text-align:center">${number}</div>
      </div>`,
    className: "",
    iconSize: [40, 52],
    iconAnchor: [20, 20],
  });
}

// GADTECH Concrete plant — 1061a Cambridge Rd, Cambridge TAS 7170
const CAMBRIDGE = [-42.835973, 147.450342];   // v3.15b · exact plant location (1061A Cambridge Rd)
const HAUL_RADIUS_M = 25000; // 25 km delivery zone

export default function MapPage() {
  const { currentPlant, plantId } = usePlant();
  const [customers, setCustomers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [liveStatus, setLiveStatus] = useState("connecting");
  const [layer, setLayer] = useState(() => resolveLayer(localStorage.getItem("gadtech_map_layer")));
  const [headingUp, setHeadingUp] = useState(() => localStorage.getItem("gadtech_map_heading_up") === "1");
  const { heading, source: headingSource, position: myPos } = useDeviceHeading(headingUp);
  // v3.11 · efficiency pass — default hide Available/Out-of-Service trucks
  const [showAllTrucks, setShowAllTrucks] = useState(() => localStorage.getItem("gadtech_map_show_all") === "1");
  // Live Fleet Replay — scrub back through a day's real GPS history.
  const [replayOn, setReplayOn] = useState(false);
  const [replayDate, setReplayDate] = useState("");   // "" → backend uses plant-local today
  const [replay, setReplay] = useState(null);     // { start, end, trucks }
  const [cursor, setCursor] = useState(0);         // epoch ms being viewed
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const [replayLoading, setReplayLoading] = useState(false);
  const [snapRoads, setSnapRoads] = useState(() => localStorage.getItem("gadtech_replay_snap") === "1");
  // Dispute mode — isolate one truck + jump to a docket's time window.
  const [replayTruckId, setReplayTruckId] = useState("");
  const [replayDockets, setReplayDockets] = useState([]);
  const [replayWindow, setReplayWindow] = useState(null);   // { from, to, label }
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkRef = useRef(null);
  const wsRef = useRef(null);
  const debounceRef = useRef(null);

  // Deep-link: /map?replay=1&date=&truck=&docket=&from=&to= — open straight to a dispute view.
  useEffect(() => {
    if (searchParams.get("replay") !== "1") return;
    deepLinkRef.current = {
      truck: searchParams.get("truck") || "",
      docket: searchParams.get("docket") || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
    };
    const d = searchParams.get("date");
    if (d) setReplayDate(d);
    setReplayOn(true);
    // consume params so a later toggle doesn't re-trigger
    const sp = new URLSearchParams(searchParams);
    ["replay", "date", "truck", "docket", "from", "to"].forEach((k) => sp.delete(k));
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the day's history whenever replay opens, the date changes, or plant changes.
  useEffect(() => {
    if (!replayOn) { setPlaying(false); return; }
    setReplayLoading(true);
    const params = new URLSearchParams();
    if (replayDate) params.set("date", replayDate);
    if (plantId) params.set("plant_id", plantId);
    if (snapRoads) params.set("snap", "1");
    const qs = params.toString();
    api.get(`/trucks/replay${qs ? "?" + qs : ""}`).then((r) => {
      setReplay(r.data);
      if (!replayDate && r.data.date) setReplayDate(r.data.date);   // adopt plant-local today
      setCursor(r.data.start ? Date.parse(r.data.start) : 0);
      setPlaying(false);
    }).catch(() => setReplay({ start: null, end: null, trucks: [] })).finally(() => setReplayLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayOn, replayDate, plantId, snapRoads]);

  // Dockets for the viewed day (for the dispute jump-list).
  useEffect(() => {
    if (!replayOn) { setReplayDockets([]); return; }
    const params = new URLSearchParams();
    if (replayDate) params.set("date", replayDate);
    if (plantId) params.set("plant_id", plantId);
    const qs = params.toString();
    api.get(`/trucks/replay/dockets${qs ? "?" + qs : ""}`)
      .then((r) => setReplayDockets(r.data.dockets || []))
      .catch(() => setReplayDockets([]));
  }, [replayOn, replayDate, plantId]);

  // Apply a pending deep-link once the day's history has loaded.
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || !replay?.trucks) return;
    if (dl.truck && replay.trucks.some((t) => t.id === dl.truck)) setReplayTruckId(dl.truck);
    if (dl.from) {
      const from = Date.parse(dl.from), to = dl.to ? Date.parse(dl.to) : from;
      if (Number.isFinite(from)) { setReplayWindow({ from, to, label: "Linked window" }); setCursor(from); setPlaying(false); }
    }
    if (!dl.docket) deepLinkRef.current = null;   // keep for docket-apply effect if a docket was given
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay]);

  // Apply a pending deep-link docket once dockets have loaded.
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || !dl.docket || !replayDockets.length) return;
    const d = replayDockets.find((x) => x.id === dl.docket || String(x.docket_no) === dl.docket);
    if (d) jumpToDocket(d);
    deepLinkRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayDockets]);

  // Playback loop — each 200ms tick advances `speed` minutes of the recording.
  useEffect(() => {
    if (!replayOn || !playing || !replay?.end) return undefined;
    const end = replayWindow ? replayWindow.to : Date.parse(replay.end);
    const id = setInterval(() => {
      setCursor((c) => {
        const next = c + speed * 60 * 1000;
        if (next >= end) { setPlaying(false); return end; }
        return next;
      });
    }, 200);
    return () => clearInterval(id);
  }, [replayOn, playing, speed, replay, replayWindow]);

  // Position of every truck at the scrubbed moment, plus its path so far.
  // When a single truck is isolated (dispute mode), draw its FULL route — no cap.
  const replayTracks = useMemo(() => {
    if (!replayOn || !replay?.trucks) return [];
    const src = replayTruckId ? replay.trucks.filter((t) => t.id === replayTruckId) : replay.trucks;
    return src.map((t) => {
      const pts = t.points.filter((p) => Date.parse(p.ts) <= cursor);
      if (!pts.length) return null;
      const last = pts[pts.length - 1];
      return {
        id: t.id, number: t.number, driver_name: "", status: last.status,
        lat: last.lat, lng: last.lng, heading: null, speed_kph: null,
        location_updated_at: last.ts, location_source: "replay",
        trail: replayTruckId ? pts : pts.slice(-120), route_to_site: [],
      };
    }).filter(Boolean);
  }, [replayOn, replay, cursor, replayTruckId]);

  // Full-day route + highlighted docket window for the isolated truck.
  const selectedTruck = useMemo(
    () => (replayTruckId ? (replay?.trucks || []).find((t) => t.id === replayTruckId) : null),
    [replay, replayTruckId],
  );
  const fullRoute = useMemo(
    () => (selectedTruck ? selectedTruck.points.map((p) => [p.lat, p.lng]) : []),
    [selectedTruck],
  );
  const windowRoute = useMemo(() => {
    if (!selectedTruck || !replayWindow) return [];
    return selectedTruck.points
      .filter((p) => { const ts = Date.parse(p.ts); return ts >= replayWindow.from && ts <= replayWindow.to; })
      .map((p) => [p.lat, p.lng]);
  }, [selectedTruck, replayWindow]);

  const jumpToDocket = (d) => {
    if (d.truck_id) setReplayTruckId(d.truck_id);
    const from = Date.parse(d.loaded_at);
    const to = d.completed_at ? Date.parse(d.completed_at) : (d.arrived_at ? Date.parse(d.arrived_at) : from);
    setReplayWindow({ from, to: Number.isFinite(to) && to > from ? to : from + 30 * 60 * 1000, label: d.docket_no ? `Docket #${d.docket_no}` : "Delivery" });
    setCursor(Number.isFinite(from) ? from : (replay?.start ? Date.parse(replay.start) : 0));
    setPlaying(false);
  };
  const playWindow = () => { if (!replayWindow) return; setCursor(replayWindow.from); setPlaying(true); };
  const clearWindow = () => { setReplayWindow(null); };

  const docketsForView = useMemo(
    () => (replayTruckId ? replayDockets.filter((d) => d.truck_id === replayTruckId) : replayDockets),
    [replayDockets, replayTruckId],
  );
  const fmtHM = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

  const setLayerPersist = (v) => {
    setLayer(v);
    localStorage.setItem("gadtech_map_layer", v);
  };

  const setHeadingPersist = (v) => {
    setHeadingUp(v);
    localStorage.setItem("gadtech_map_heading_up", v ? "1" : "0");
  };

  const tiles = TILE_LAYERS[layer];

  const loadTracks = () => {
    const params = new URLSearchParams();
    if (plantId) params.set("plant_id", plantId);
    if (!showAllTrucks) params.set("active_only", "true");
    const qs = params.toString();
    api.get(`/trucks/tracks${qs ? "?" + qs : ""}`).then((r) => setTracks(r.data));
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    loadTracks();
    const poll = setInterval(() => { if (!document.hidden) loadTracks(); }, 10000); // fallback polling (WS may be blocked by proxy)
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, showAllTrucks]);

  // Live truck locations via existing dispatch WS ("truck.location" events)
  useEffect(() => {
    let ws;
    let closed = false;
    let retry = 0;

    const debouncedReload = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(loadTracks, 400);
    };

    const connect = () => {
      if (closed) return;
      setLiveStatus("connecting");
      try {
        ws = new WebSocket(`${WS_BASE}/ws/dispatch`);
      } catch (e) { setLiveStatus("offline"); return; }
      wsRef.current = ws;
      ws.onopen = () => { retry = 0; setLiveStatus("live"); };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (plantId && evt.plant_id && evt.plant_id !== plantId) return;
          if (evt.type === "truck.location") {
            // Optimistic: update in place from the event; also debounce a full refresh for the trail.
            setTracks((prev) => {
              const idx = prev.findIndex((t) => t.id === evt.truck_id);
              const nowIso = evt.ts || new Date().toISOString();
              if (idx === -1) { debouncedReload(); return prev; }
              const clone = prev.slice();
              const t = { ...clone[idx], lat: evt.lat, lng: evt.lng, heading: evt.heading ?? clone[idx].heading, speed_kph: evt.speed_kph ?? clone[idx].speed_kph, location_source: evt.source, location_updated_at: nowIso, status: evt.status || clone[idx].status };
              t.trail = [...(t.trail || []), { lat: evt.lat, lng: evt.lng, ts: nowIso }].slice(-25);
              clone[idx] = t;
              return clone;
            });
          } else if (["truck.status", "job.assigned", "job.unassigned", "docket.delivered", "batch.completed"].includes(evt.type)) {
            debouncedReload();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setLiveStatus("offline");
        if (closed) return;
        retry = Math.min(retry + 1, 5);
        setTimeout(connect, 1000 * retry);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      closed = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [plantId]);

  const sites = customers.flatMap((c) =>
    (c.sites || [])
      .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
      .map((s) => ({ ...s, customer_name: c.name }))
  );

  const activeTrucks = tracks.filter((t) => ["en_route", "on_site", "returning", "loading", "slumping"].includes(t.status));
  const shown = replayOn ? replayTracks : tracks;

  return (
    <div className="p-6 lg:p-8 space-y-4 h-full flex flex-col" data-testid="map-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag flex items-center gap-2">
            GEOGRAPHY
            <span
              data-testid="map-ws-pill"
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${
                liveStatus === "live" ? "border-primary-orange text-primary-orange" :
                liveStatus === "connecting" ? "border-industrial text-muted-industrial" :
                "border-red-700 text-red-400"
              }`}
            >
              <Radio className={`w-2.5 h-2.5 ${liveStatus === "live" ? "pulse-orange" : ""}`} />
              {liveStatus === "live" ? "LIVE" : liveStatus === "connecting" ? "CONNECTING" : "OFFLINE"}
            </span>
          </div>
          <h1 className="heading text-3xl font-black uppercase">Delivery <span className="text-primary-orange">Map</span></h1>
          <div className="text-xs text-muted-industrial mt-1">
            Plant hub · Cambridge TAS · {sites.length} geolocated site{sites.length === 1 ? "" : "s"} · <span className="text-primary-orange" data-testid="active-trucks-count">{activeTrucks.length}</span> live truck{activeTrucks.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setReplayOn((v) => !v)}
            data-testid="map-replay-toggle"
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 mono text-[10px] uppercase tracking-widest transition ${replayOn ? "border-primary-orange bg-primary-orange/10 text-primary-orange" : "border-industrial bg-surface text-ink hover:border-primary-orange"}`}
            title="Replay the day's fleet movements"
          >
            <History className="w-3.5 h-3.5" /> {replayOn ? "Live view" : "Replay"}
          </button>
          <MapStyleToggle layer={layer} onChange={setLayerPersist} />
          <HeadingToggle on={headingUp} onChange={setHeadingPersist} source={headingSource} />
        </div>
        <label
          className="flex items-center gap-1.5 border border-industrial bg-surface px-2.5 py-1.5 cursor-pointer hover:border-primary-orange transition"
          data-testid="map-show-all-toggle"
          title="When OFF, only trucks that are loading, en-route, on-site, discharging or returning are drawn. Idle/OOS trucks are skipped to save GPS + route API calls."
        >
          <input
            type="checkbox"
            checked={showAllTrucks}
            onChange={(e) => {
              setShowAllTrucks(e.target.checked);
              localStorage.setItem("gadtech_map_show_all", e.target.checked ? "1" : "0");
            }}
            data-testid="map-show-all-cb"
            className="w-3 h-3"
          />
          <span className="mono text-[10px] uppercase tracking-widest text-ink">Show all trucks</span>
        </label>
      </div>

      {replayOn && (
        <FleetReplayBar
          date={replayDate}
          onDate={setReplayDate}
          start={replay?.start ? Date.parse(replay.start) : NaN}
          end={replay?.end ? Date.parse(replay.end) : NaN}
          cursor={cursor}
          onCursor={(ms) => { setPlaying(false); setCursor(ms); }}
          playing={playing}
          onTogglePlay={() => setPlaying((p) => !p)}
          speed={speed}
          onSpeed={setSpeed}
          loading={replayLoading}
          truckCount={replay?.trucks?.length || 0}
        />
      )}

      {replayOn && (
        <div className="card-industrial px-4 py-3 flex flex-col gap-2" data-testid="replay-dispute-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="label-tag flex items-center gap-1"><Crosshair className="w-3 h-3" /> DISPUTE</span>
            <select
              value={replayTruckId}
              onChange={(e) => { setReplayTruckId(e.target.value); setReplayWindow(null); }}
              data-testid="replay-truck-select"
              className="bg-surface border border-industrial px-2 py-1 mono text-xs text-ink focus:border-primary-orange outline-none"
            >
              <option value="">All trucks ({replay?.trucks?.length || 0})</option>
              {(replay?.trucks || []).map((t) => (<option key={t.id} value={t.id}>{t.number}</option>))}
            </select>
            {replayTruckId && (
              selectedTruck ? (
                <span className="mono text-[10px] uppercase tracking-widest text-primary-orange" data-testid="replay-isolated">
                  Showing {selectedTruck.number}'s full route
                </span>
              ) : (
                <span className="mono text-[10px] uppercase tracking-widest text-red-400" data-testid="replay-isolated-nogps">
                  No GPS recorded for this truck this day
                </span>
              )
            )}
            <label className="ml-auto flex items-center gap-1.5 cursor-pointer select-none" data-testid="replay-snap-toggle">
              <input type="checkbox" checked={snapRoads}
                onChange={(e) => { setSnapRoads(e.target.checked); localStorage.setItem("gadtech_replay_snap", e.target.checked ? "1" : "0"); }}
                className="accent-[color:var(--primary-orange,#F57C00)]" />
              <span className="mono text-[10px] uppercase tracking-widest text-ink">Snap to road</span>
              {snapRoads && replay && replay.snapped === false && (
                <span className="mono text-[9px] uppercase tracking-widest text-amber-400" title="Enable the Roads API on your Google Maps key">· raw (Roads API off)</span>
              )}
            </label>
            {replayWindow && (
              <span className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest border border-primary-orange text-primary-orange px-2 py-1" data-testid="replay-window-badge">
                {replayWindow.label} · {fmtHM(new Date(replayWindow.from).toISOString())}→{fmtHM(new Date(replayWindow.to).toISOString())}
                <button type="button" onClick={playWindow} title="Play this window" data-testid="replay-play-window" className="hover:text-ink"><PlayIcon className="w-3 h-3" /></button>
                <button type="button" onClick={clearWindow} title="Clear window" data-testid="replay-clear-window" className="hover:text-ink"><XIcon className="w-3 h-3" /></button>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 max-h-24 overflow-auto" data-testid="replay-docket-list">
            {docketsForView.length === 0 ? (
              <span className="mono text-[10px] text-muted-industrial">No dockets recorded this day{replayTruckId ? " for this truck" : ""}.</span>
            ) : docketsForView.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => jumpToDocket(d)}
                data-testid={`replay-docket-${d.docket_no || d.id}`}
                title={`Jump to ${d.customer_name || "delivery"} · loaded ${fmtHM(d.loaded_at)}${d.completed_at ? ` · done ${fmtHM(d.completed_at)}` : ""}`}
                className={`mono text-[10px] px-2 py-1 border transition ${replayWindow?.label === `Docket #${d.docket_no}` ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial hover:text-ink hover:border-primary-orange"}`}
              >
                #{d.docket_no || "—"} · {fmtHM(d.loaded_at)} · {d.truck_number || "?"} · {(d.customer_name || "").slice(0, 18)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card-industrial flex-1 min-h-[500px] overflow-hidden" data-testid="map-container">
       <RotateFrame bearing={headingUp ? heading : 0} className={tiles.invert ? "map-dark-tiles" : ""}>
        <MapContainer center={CAMBRIDGE} zoom={10} style={{ height: "100%", width: "100%", background: tiles.bg }}>
          <InvalidateOnChange dep={headingUp && heading !== 0} />
          <TileLayer
            key={layer}
            url={tiles.url}
            attribution={tiles.attr}
            subdomains={tiles.subdomains}
            maxZoom={20}
          />
          {/* Hybrid already has labels baked in by Google */}
          <Marker position={CAMBRIDGE} icon={plantIcon}>
            <Popup>
              <b>{currentPlant?.name || "ArmorBatch"}</b><br />
              1061a Cambridge Rd, Cambridge TAS 7170
            </Popup>
          </Marker>
          {/* 25 km haul radius (delivery coverage guide) */}
          <Circle center={CAMBRIDGE} radius={HAUL_RADIUS_M} pathOptions={{ color: "#F57C00", weight: 1, fillOpacity: 0.05, dashArray: "6 6" }} />
          {/* v3.15d · Plant geofence — used for depart/arrive triggers (default 75m) */}
          <Circle center={CAMBRIDGE} radius={currentPlant?.geofence_radius_m ?? 75} pathOptions={{ color: "#F57C00", weight: 2, fillOpacity: 0.35 }} />

          {sites.map((s) => (
            <Marker key={s.id} position={[s.lat, s.lng]} icon={siteIcon}>
              <Popup>
                <b>{s.customer_name}</b><br />
                {s.name}<br />
                <span style={{ color: "#71717A" }}>{s.address}</span>
              </Popup>
            </Marker>
          ))}

          {/* Dispute mode — isolated truck's full day route + highlighted docket window */}
          {replayOn && fullRoute.length > 1 && (
            <Polyline positions={fullRoute} pathOptions={{ color: "#F57C00", weight: 3, opacity: 0.35 }} />
          )}
          {replayOn && windowRoute.length > 1 && (
            <Polyline positions={windowRoute} pathOptions={{ color: "#22C55E", weight: 5, opacity: 0.95 }} />
          )}

          {/* Live trucks (or replay snapshot): trail polyline + heading marker + Google-suggested driving route */}
          {shown.map((t) => (
            t.lat != null && t.lng != null && (
              <Fragment key={t.id}>
                {t.trail && t.trail.length > 1 && (
                  <Polyline
                    positions={t.trail.map((p) => [p.lat, p.lng])}
                    pathOptions={{ color: STATUS_COLOR[t.status] || "#F57C00", weight: 2, opacity: 0.55, dashArray: "4 4" }}
                  />
                )}
                {Array.isArray(t.route_to_site) && t.route_to_site.length > 1 && (
                  <Polyline
                    positions={t.route_to_site}
                    pathOptions={{ color: "#F57C00", weight: 4, opacity: 0.85 }}
                  />
                )}
                <Marker position={[t.lat, t.lng]} icon={truckIcon(t)}>
                  <Popup>
                    <b>{t.number}</b> — {t.driver_name || "—"}<br />
                    <span style={{ color: "#F57C00", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>{(t.status || "").replace("_", " ")}</span><br />
                    <span style={{ color: "#71717A", fontSize: 11 }}>
                      {t.speed_kph != null ? `${Math.round(t.speed_kph)} km/h` : "stopped"} · {t.location_source || "—"}<br />
                      Updated {t.location_updated_at ? new Date(t.location_updated_at).toLocaleTimeString() : "—"}
                    </span>
                  </Popup>
                </Marker>
              </Fragment>
            )
          ))}
          {headingUp && myPos && (
            <Marker position={myPos} icon={meIcon}>
              <Popup><b>You are here</b><br /><span style={{ color: "#71717A", fontSize: 11 }}>Heading {Math.round(heading)}° · {headingSource || "waiting for fix"}</span></Popup>
            </Marker>
          )}
        </MapContainer>
       </RotateFrame>
      </div>
      <div className="text-xs mono text-muted-industrial">
        <span className="text-primary-orange">Live trucks</span> stream from Driver Portal GPS when active, otherwise simulated toward the delivery site. Dashed ring = 25 km haul radius. <span className="text-primary-orange">Heading up</span> rotates the map to the way you're facing — labels stay upright.
      </div>
    </div>
  );
}
